/** A field whose value is an ordered list of record ids (an activity's nearby activities,
 *  `activity_list`): the items in order — numbered, by name — with a picker under them
 *  that adds one, and per item Move up / Move down / Remove (Restore once removed). Every
 *  edit is ONE value change, so ONE undo step; the list edits are the collections'
 *  (`rows.ts`: `withId`, `movedId`, `placedAsStored`, …).
 *
 *  - Names come from the record kind's resolver (`labelOf`, ids → names: its stored items'
 *    names); a pick is named by the picker's option; anything else reads "#<id>".
 *  - Each item says what the draft did to it, in an icon AND words (One-Wording with the
 *    contacts list): **Added**, **Moved · was 3** (with Revert), **Removed** (struck, with
 *    Restore). Only those rows take the amber tint; kept rows stay plain.
 *  - The ledger reads the change as one line per item it touches (`ledgerLines`, Changed-
 *    Items-Only), each with its own revert; the widget marks its own items (`owns: 'items'`).
 *  - Keyboard: every control is a tab stop; Alt+↑ / Alt+↓ moves the item whose control has
 *    the focus, and the focus moves with it. Moves and removals are announced.
 *  - Zero re-layout: view mode (`View`) shows the same 32 px rows, numbered, and keeps the
 *    picker's 40 px row.
 *
 *  Create a widget once per record kind (module scope or a memo): a new definition per
 *  render would remount it. */
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { Box, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { useCallback, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from 'react';

import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';

import { asIdList, movedId, placedAsStored, withId, withoutId } from '../../collections/rows';
import { useRecordEditor } from '../../editor/context';
import type { RecordField, RecordOptionRef, RecordWidget, RecordWidgetProps } from '../../editor/types';
import { CHANGED_TINT } from '../FieldSlot';
import { RestoreButton, visuallyHidden } from '../RowMarks';

import { orderedIdItems, orderedIdLedgerLines, type OrderedIdItem } from './orderedIdListModel';
import { refMeta, type RecordOptionLoader } from './pickers';

export interface OrderedIdListOptions {
  /** ids → names: the record kind's resolver (its stored items' names, from the detail).
   *  Null / undefined: not known to it — a pick's option names it, else "#<id>". */
  labelOf: (id: number) => string | null | undefined;
  /** The picker's search (the record kind's catalog picker). */
  load: RecordOptionLoader;
  /** The picker's placeholder ("Add a nearby activity"). */
  placeholder: string;
  /** Browsable before typing. Default true. */
  fetchOnEmpty?: boolean;
}

const ROW_SX = { display: 'flex', alignItems: 'center', gap: 1, minHeight: 32, px: 0.5, borderRadius: 1, minWidth: 0 } as const;
const NUMBER_SX = { width: 24, flex: 'none', textAlign: 'right', fontVariantNumeric: 'tabular-nums' } as const;
const ADD_ROW_PX = 40;

const refKey = (r: RecordOptionRef) => r.id;
const refLabel = (r: RecordOptionRef) => r.label;
const NO_REFS: RecordOptionRef[] = [];

function StateWord({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flex: 'none' }}>
      {icon}
      {children}
    </Typography>
  );
}

function ItemsList({
  items,
  nameOf,
  labelledBy,
  label,
  describedBy,
  renderEnd,
  listRef,
}: {
  items: OrderedIdItem[];
  nameOf: (id: number) => string;
  labelledBy?: string;
  label?: string;
  describedBy?: string;
  renderEnd?: (item: OrderedIdItem, name: string) => ReactNode;
  listRef?: Ref<HTMLOListElement>;
}) {
  return (
    <Box
      component="ol"
      ref={listRef}
      {...(labelledBy ? { 'aria-labelledby': labelledBy } : { 'aria-label': label })}
      {...(describedBy ? { 'aria-describedby': describedBy } : {})}
      sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.25 }}
    >
      {items.length === 0 && (
        <Box component="li" sx={ROW_SX}>
          <Typography variant="body2" color="text.secondary">
            None
          </Typography>
        </Box>
      )}
      {items.map((item) => {
        const name = nameOf(item.id);
        const removed = item.state === 'removed';
        const marked = item.state !== 'kept';
        return (
          <Box
            component="li"
            key={item.id}
            data-id-row={item.id}
            data-state={item.state}
            sx={[ROW_SX, marked && { bgcolor: CHANGED_TINT }]}
          >
            <Typography variant="body2" color="text.secondary" sx={NUMBER_SX}>
              {item.position ?? ''}
            </Typography>
            <Typography
              variant="body2"
              noWrap
              title={name}
              sx={{ flex: '1 1 auto', minWidth: 0, ...(removed ? { textDecoration: 'line-through', color: 'text.secondary' } : {}) }}
            >
              {name}
            </Typography>
            {renderEnd?.(item, name)}
          </Box>
        );
      })}
    </Box>
  );
}

export function createOrderedIdListWidget({ labelOf, load, placeholder, fetchOnEmpty = true }: OrderedIdListOptions): RecordWidget {
  /** Names the picker taught this field, for the items `labelOf` does not know. */
  const picked = new Map<number, string>();
  const nameOf = (id: number) => labelOf(id) ?? picked.get(id) ?? `#${id}`;

  function OrderedIdListInput(p: RecordWidgetProps) {
    const { field, rec, onChange } = p;
    const { rememberOptions } = useRecordEditor();
    const hintId = useId();
    const listRef = useRef<HTMLOListElement>(null);
    const [said, setSaid] = useState('');
    const stored = asIdList(p.baseline);
    const list = asIdList(p.value);
    const items = orderedIdItems(stored, list, true);
    const queryFn = useCallback((q: string, signal: AbortSignal) => load(field, rec, q, signal), [field, rec]);

    // After an edit the focus stays with its item: the same control if it still can act,
    // else its other move, else any of its controls; an item that left the list hands it to
    // the next item, else to the picker.
    const pendingFocus = useRef<{ id: number; control: string; index: number } | null>(null);
    useLayoutEffect(() => {
      const f = pendingFocus.current;
      if (!f) return;
      pendingFocus.current = null;
      const root = listRef.current;
      const row = root?.querySelector<HTMLElement>(`[data-id-row="${f.id}"]`);
      const enabled = (el: ParentNode | null | undefined, ...sels: string[]) =>
        el?.querySelector<HTMLElement>(sels.map((sel) => `${sel}:not(:disabled)`).join(', ')) ?? null;
      const ANY = ['button[data-control]', 'button[data-restore]'];
      const control = (c: string) => (c === 'restore' ? 'button[data-restore]' : `[data-control="${c}"]`);
      const other = f.control === 'up' ? 'down' : f.control === 'down' ? 'up' : null;
      const next =
        enabled(row, control(f.control)) ??
        (other ? enabled(row, control(other)) : null) ??
        enabled(row, ...ANY) ??
        enabled(root?.querySelectorAll('[data-id-row]')[f.index], ...ANY) ??
        document.getElementById(p.id);
      next?.focus();
    });

    const change = (next: number[], label: string, announce: string, focus: { id: number; control: string } | null) => {
      if (focus) pendingFocus.current = { ...focus, index: list.indexOf(focus.id) };
      onChange(next, { label });
      setSaid(announce);
    };
    const move = (id: number, delta: -1 | 1, control: string) => {
      const next = movedId(list, id, delta);
      if (!next) return;
      const name = nameOf(id);
      change(next, `Move ${name} ${delta < 0 ? 'up' : 'down'} in ${field.label}`, `${name} moved to ${next.indexOf(id) + 1} of ${next.length}`, {
        id,
        control,
      });
    };
    const remove = (id: number) => {
      const name = nameOf(id);
      change(withoutId(list, id), `Remove ${name} from ${field.label}`, `${name} removed`, { id, control: 'restore' });
    };
    const restore = (id: number) => {
      const name = nameOf(id);
      change(placedAsStored(list, stored, id), `Restore ${name} to ${field.label}`, `${name} restored`, { id, control: 'remove' });
    };
    const add = (refs: RecordOptionRef[]) => {
      const fresh = refs.filter((r) => typeof r.id === 'number' && !list.includes(r.id));
      if (fresh.length === 0) return;
      for (const r of fresh) picked.set(r.id as number, r.label);
      rememberOptions(field.key, fresh);
      let next = list;
      for (const r of fresh) {
        const id = r.id as number;
        // A stored item picked again goes back to its place.
        next = stored.includes(id) ? placedAsStored(next, stored, id) : withId(next, id);
      }
      const names = fresh.map((r) => r.label).join(', ');
      change(next, `Add ${names} to ${field.label}`, `${names} added`, null);
    };

    const onKeyDown = (item: OrderedIdItem) => (e: KeyboardEvent<HTMLElement>) => {
      if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') || item.state === 'removed') return;
      e.preventDefault();
      const control = (e.target as HTMLElement).dataset.control ?? (e.key === 'ArrowUp' ? 'up' : 'down');
      move(item.id, e.key === 'ArrowUp' ? -1 : 1, control);
    };

    const last = list.length - 1;
    return (
      <Box sx={{ width: '100%', minWidth: 0 }}>
        <ItemsList
          items={items}
          nameOf={nameOf}
          labelledBy={p.labelId}
          describedBy={hintId}
          listRef={listRef}
          renderEnd={(item, name) => {
            const index = list.indexOf(item.id);
            return (
              <Box onKeyDown={onKeyDown(item)} sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 'none' }}>
                {item.state === 'added' && (
                  <StateWord icon={<AddCircleOutlineIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main' }} />}>Added</StateWord>
                )}
                {item.state === 'moved' && (
                  <StateWord icon={<SwapVertIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main' }} />}>
                    Moved · was {item.storedPosition} ·{' '}
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      data-control="revert"
                      aria-label={`Move ${name} back to ${item.storedPosition}`}
                      onClick={() => {
                        const next = placedAsStored(list, stored, item.id);
                        change(next, `Move ${name} back in ${field.label}`, `${name} moved to ${next.indexOf(item.id) + 1} of ${next.length}`, {
                          id: item.id,
                          control: 'up',
                        });
                      }}
                    >
                      Revert
                    </Link>
                  </StateWord>
                )}
                {item.state === 'removed' ? (
                  <>
                    <StateWord icon={<DeleteOutlineIcon aria-hidden sx={{ fontSize: 14 }} />}>Removed</StateWord>
                    <RestoreButton name={name} hint="keep it in the list" onRestore={() => restore(item.id)} />
                  </>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      size="small"
                      data-control="up"
                      aria-label={`Move ${name} up`}
                      disabled={p.disabled || index <= 0}
                      onClick={() => move(item.id, -1, 'up')}
                      sx={{ p: 0.25 }}
                    >
                      <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      data-control="down"
                      aria-label={`Move ${name} down`}
                      disabled={p.disabled || index >= last}
                      onClick={() => move(item.id, 1, 'down')}
                      sx={{ p: 0.25 }}
                    >
                      <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Tooltip title={item.state === 'added' ? 'Remove — it was not saved yet' : 'Remove — taken off on save'}>
                      <IconButton
                        size="small"
                        data-control="remove"
                        aria-label={`Remove ${name}`}
                        disabled={p.disabled}
                        onClick={() => remove(item.id)}
                        sx={{ p: 0.25 }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            );
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: ADD_ROW_PX, mt: 0.5, minWidth: 0 }}>
          <Box sx={{ flex: '0 1 360px', minWidth: 160 }}>
            <RemoteAutocomplete<RecordOptionRef>
              id={p.id}
              size="small"
              multiple
              // Picks are added to the list above, never held here.
              value={NO_REFS}
              renderTags={() => null}
              queryFn={queryFn}
              getOptionKey={refKey}
              getOptionLabel={refLabel}
              getOptionMeta={refMeta}
              getOptionDisabled={(r) => typeof r.id === 'number' && list.includes(r.id)}
              fetchOnEmpty={fetchOnEmpty}
              placeholder={placeholder}
              disabled={p.disabled}
              error={p.error}
              inputProps={{ 'aria-describedby': p.describedBy }}
              onChange={(v) => add((v as RecordOptionRef[] | null) ?? [])}
            />
          </Box>
          <Typography id={hintId} variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
            Alt+↑ / Alt+↓ moves an item
          </Typography>
        </Box>
        <Box role="status" sx={visuallyHidden}>
          {said}
        </Box>
      </Box>
    );
  }

  /** The stored list read-only, on the edit rows' geometry; the picker's row kept empty. */
  function OrderedIdListView({ field, value }: { field: RecordField; value: unknown }) {
    return (
      <Box sx={{ width: '100%', minWidth: 0 }}>
        <ItemsList items={orderedIdItems(asIdList(value), asIdList(value), false)} nameOf={nameOf} label={field.label} />
        <Box aria-hidden sx={{ minHeight: ADD_ROW_PX, mt: 0.5 }} />
      </Box>
    );
  }

  return Object.assign(OrderedIdListInput, {
    formatChange: (value: unknown) => asIdList(value).map(nameOf).join(', '),
    // Any list of ids may be saved — an empty one too; the server checks whose they are.
    checkValue: () => null,
    ledgerLines: (from: unknown, to: unknown) => orderedIdLedgerLines(asIdList(from), asIdList(to), nameOf),
    owns: 'items' as const,
    View: OrderedIdListView,
  });
}
